import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShoppingBasket, Loader2, Phone, Lock } from "lucide-react";

export default function Login() {
  usePageMeta("Login", "Log in to your Nicee account.");
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login({ mobile, password });
      navigate(location.state?.from || "/", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto flex max-w-md flex-col px-4 py-16 sm:px-6">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl border border-border/60 bg-white p-8 shadow-lift">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brand text-ink">
          <ShoppingBasket className="h-6 w-6" />
        </span>
        <h1 className="mt-5 text-center font-heading text-2xl font-bold text-ink">Welcome back</h1>
        <p className="mt-1 text-center text-sm text-ink-soft">Log in with your mobile number</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label className="text-ink-soft">Mobile number</Label>
            <div className="relative mt-1.5">
              <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
              <Input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="10-digit mobile" className="rounded-xl pl-9" required data-testid="login-mobile" />
            </div>
          </div>
          <div>
            <Label className="text-ink-soft">Password</Label>
            <div className="relative mt-1.5">
              <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password" className="rounded-xl pl-9" required data-testid="login-password" />
            </div>
          </div>
          {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full rounded-full bg-brand text-ink hover:bg-brand-hover" data-testid="login-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log in"}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-ink-soft">
          New here?{" "}
          <Link to="/register" className="font-semibold text-brand hover:underline" data-testid="go-register">Create an account</Link>
        </p>
      </motion.div>
    </div>
  );
}
